import { ActionIcon, Group, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { AudioLines, Check, Download, Expand, Save } from "lucide-react";

import type { GenerationOutput, Job } from "../../api/generated/model";
import { useDownloadOutput } from "../../platform/jobs/download-output";
import { useRowActions } from "../../platform/jobs/GenerationActions";
import { expiredLine, isExpired } from "../../platform/jobs/expired";
import { outputKey } from "../../platform/jobs/output-picks";
import { SaveName } from "../../platform/jobs/SaveName";
import { isSettled } from "../../platform/jobs/status";
import { formatDateTime } from "../../ui/format";
import { MediaCard } from "../../ui/MediaCard";
import { SoundWave } from "../../ui/SoundWave";
import { MiniPlayer } from "./MiniPlayer";
import {
  FAILED_TEXT,
  FAILED_TITLE,
  GENERATING,
  SOUND_LABEL,
  generationFileName,
  soundFileName,
  soundName,
  soundReady,
} from "./sound-jobs";
import classes from "./sounds.module.css";

export interface SoundGenerationProps {
  teamId: string;
  brandId: string;
  job: Job;
  /** The output of this job playing in the page's player, or none. */
  playing: number | null;
  /** Plays an output; with none, closes the player. */
  onPlay: (index: number | null) => void;
  /** Opens a finished sound in the lightbox (its ⤢ "Open preview"). */
  onPreview: (index: number) => void;
  /** Reads the list again, for a fresh link once one expired. */
  readAgain: () => void;
}

/**
 * One generation on the Sounds page (live's `my-generations/generation`): its kind and day, and
 * live's "Save generation", "Download generation" (`sound-<id>`) and "Delete generation" at the
 * right; then its sound as live's card in a row of five, or live's failed tile. While it plays,
 * the page's player carries the card's Save and Download. A finished sound's ⤢ opens it in the
 * lightbox (SA1 decision 8); a click on the card plays it, as live's.
 */
export function SoundGeneration(props: SoundGenerationProps) {
  const { teamId, brandId, job, playing, onPlay, onPreview, readAgain } = props;
  const actions = useRowActions({
    teamId,
    brandId,
    jobs: [job],
    readAgain,
    toProject: false,
    fileName: generationFileName,
    askName: true,
  });
  const label = SOUND_LABEL[job.type] ?? job.type;
  const when = formatDateTime(job.created_at);
  const ended = job.status === "failed" || job.status === "expired" || job.status === "canceled";
  const going = !isSettled(job.status);
  const sounds = job.outputs.length === 0 ? [null] : job.outputs;
  const heard = job.outputs.find((output) => output.index === playing && soundReady(job, output));
  const savedOf = (output: GenerationOutput) =>
    actions.saved.has(outputKey({ jobId: job.id, index: output.index }));
  const cardActions = (output: GenerationOutput, size: number) => (
    <SoundActions
      job={job}
      output={output}
      size={size}
      saved={savedOf(output)}
      saving={actions.saving}
      onSave={(name, done) =>
        actions.saveOne({ jobId: job.id, index: output.index }, { name, onSaved: done })
      }
      readAgain={readAgain}
    />
  );
  return (
    <article className={classes.generation} aria-label={`${label}, ${when}`}>
      <Group justify="space-between" wrap="nowrap" mb={8}>
        <Group gap={8} wrap="nowrap" miw={0}>
          <span className={classes.kind}>{label}</span>
          <Text size="xs" c="var(--cai-color-text-muted)" truncate>
            {when}
          </Text>
        </Group>
        {actions.icons}
      </Group>
      {actions.notes}
      {ended ? (
        <Stack align="center" justify="center" gap={6} className={classes.soundFailed} role="alert">
          <AudioLines size={57} strokeWidth={1.5} className={classes.soundFailedIcon} aria-hidden />
          <Text fw={500}>{FAILED_TITLE}</Text>
          <Text c="var(--cai-color-text-muted)">{job.error?.message || FAILED_TEXT}</Text>
        </Stack>
      ) : (
        <ul className={classes.sounds}>
          {sounds.map((output) => {
            const playable = output !== null && !going && soundReady(job, output);
            const name = going || output === null ? GENERATING : soundName(job);
            const lit = output !== null && output.index === playing;
            return (
              <li key={output?.index ?? "making"}>
                <MediaCard
                  saved={output !== null && savedOf(output)}
                  menu={playable ? cardActions(output, 20) : undefined}
                  corner={
                    playable ? (
                      <Tooltip label="Open preview">
                        <ActionIcon
                          variant="text-white"
                          className={classes.expand}
                          aria-label={`Open preview of ${name}`}
                          onClick={() => onPreview(output.index)}
                        >
                          <Expand size={16} aria-hidden />
                        </ActionIcon>
                      </Tooltip>
                    ) : undefined
                  }
                >
                  <UnstyledButton
                    className={classes.sound}
                    data-playing={lit || undefined}
                    aria-label={playable ? `Listen to ${name}` : name}
                    aria-pressed={playable ? lit : undefined}
                    disabled={!playable}
                    onClick={() => onPlay(output?.index ?? null)}
                  >
                    <SoundWave className={classes.tileWave} animated={going || lit} />
                    <Text className={classes.tileName} title={name}>
                      {name}
                    </Text>
                    {output !== null && isExpired(output) ? (
                      <Text size="xs" c="var(--cai-color-text-muted)">
                        {expiredLine(job)}
                      </Text>
                    ) : null}
                  </UnstyledButton>
                </MediaCard>
              </li>
            );
          })}
        </ul>
      )}
      {heard === undefined || heard.url === null ? null : (
        <MiniPlayer
          key={`${job.id}:${heard.index}`}
          url={heard.url}
          name={soundName(job)}
          durationSeconds={heard.duration_ms === null ? null : heard.duration_ms / 1000}
          actions={cardActions(heard, 18)}
          onClose={() => onPlay(null)}
          onError={readAgain}
        />
      )}
    </article>
  );
}


export interface SoundActionsProps {
  job: Job;
  output: GenerationOutput;
  size: number;
  saved: boolean;
  saving: boolean;
  onSave: (name: string, done: () => void) => void;
  readAgain: () => void;
}

/**
 * A sound's own actions (live's `SoundActions`): Save, which asks for a name first ("Add name"),
 * then "Saved" with a green check, grey, once kept; and Download, saving the file under the
 * sound's name.
 */
export function SoundActions({ job, output, size, saved, saving, onSave, readAgain }: SoundActionsProps) {
  const download = useDownloadOutput(job, output, readAgain, soundFileName(job, output));
  const saveLabel = saved ? "Saved" : "Save";
  return (
    <Group gap={4} wrap="nowrap">
      <SaveName
        label={saveLabel}
        icon={
          saved ? (
            <Check size={size} color="var(--cai-color-success)" />
          ) : (
            <Save size={size} strokeWidth={1.5} />
          )
        }
        disabled={saved || saving}
        saving={saving}
        onSave={onSave}
      />
      <Tooltip label="Download">
        <ActionIcon
          variant="transparent"
          c="inherit"
          aria-label="Download"
          loading={download.busy}
          onClick={download.start}
        >
          <Download size={size} strokeWidth={1.5} />
        </ActionIcon>
      </Tooltip>
      {download.result}
    </Group>
  );
}
